import React, { Component } from 'react'
import { FilePicker } from 'react-file-picker'
import { prefix } from '../../../i18n'
import ReactSelect from 'react-select'
import { InvoiceService } from '../../../services'
import { Link } from 'react-router-dom'
import Modal from '../shared/Modal'
import InvoiceProductFormContainer from './InvoiceProductFormContainer'
import InputKeyValue from '../shared/InputKeyValue'
import { EditPencilLine01, Trash, FileDocument, ArrowLeftLG, Download, FileDownload } from '../normalizer'
import CustomButton from '../shared/CustomButton'
import { normalizeInvoiceProduct, toProductFormInitialValues, toUnitPrice } from './invoiceProducts'

const tInvoice = prefix('invoice')

export default class InvoiceForm extends Component {
  state = {
    number: '',
    saleType: null,
    document: null,
    thereIsInvoice: false,
    errorToGetInvoice: null,
    invoiceDate: new Date(),
    products: [],
    showProductModal: false,
    editingProductIndex: null,
    removingProductIndex: null,
  }

  updateState = (stateName, mapper) => (data) => {
    this.setState({ [stateName]: mapper ? mapper(data) : data })
  }

  eventMapper = (e) => e.target.value

  // Create mode: both buttons create the invoice, and `submitForApproval` tells
  // the container whether to follow the creation with set_invoice_to_approval.
  // Without it a new invoice always stays a draft, which is what made "Enviar
  // fatura" and "Guardar rascunho" behave identically.
  handleCreate = (submitForApproval) => {
    const { number, invoiceDate, saleType, document, products } = this.state
    if (this.state.thereIsInvoice) {
      return window.alert('Já existe factura com mesmo número')
    }
    if (this.state.errorToGetInvoice) {
      return window.alert('Ocorreu um erro para verificar número de factura')
    }
    this.props.onSubmit({
      number,
      invoiceDate,
      saleType,
      document: document || {},
      products,
      submitForApproval,
    })
  }

  checkExistentInvoice = (event) => {
    event.preventDefault()
    const { campaign, client, invoice } = this.props
    if (!client) return
    InvoiceService.getInvoiceByNumber({
      campaignId: campaign.slug,
      clientId: client.slug,
      invoiceNumber: this.state.number,
    })
      .then(response => {
        // In edit mode the lookup finds the invoice being edited, which must
        // not count as a duplicate — otherwise its own number would block
        // every save.
        const found = response.status === 200 && response.data.id
        const itself = found && invoice && response.data.id === invoice.id
        this.setState({ thereIsInvoice: Boolean(found && !itself), errorToGetInvoice: null })
      })
      .catch(error => {
        if (error.response && error.response.status === 404) {
          this.setState({ thereIsInvoice: false, errorToGetInvoice: null })
        } else {
          this.setState({ thereIsInvoice: false, errorToGetInvoice: error })
        }
      })
  }

  resolveSaleType = () => {
    const { saleTypes, invoice } = this.props
    const defaultValue = saleTypes.length > 0 ? saleTypes[0] : null
    if (!invoice) return defaultValue
    return saleTypes.find(i => i.name === invoice.sale_type) || defaultValue
  }

  // In edit mode the list is fetched by the container and so arrives after
  // mount, and its identity changes again on every refetch triggered by a
  // product operation. A selection that is still valid is therefore kept
  // instead of being reset to the first option.
  componentDidUpdate(oldProps) {
    const { saleTypes } = this.props
    if (oldProps.saleTypes === saleTypes) return
    const resolved = this.resolveSaleType()
    this.setState(function(prevState) {
      const stillValid = prevState.saleType
        && saleTypes.some(function(o) { return o.id === prevState.saleType.id })
      return stillValid ? null : { saleType: resolved }
    })
  }

  componentDidMount() {
    const { invoice } = this.props
    const nextState = { saleType: this.resolveSaleType() }
    if (invoice) {
      nextState.number = invoice.invoice_number
      nextState.invoiceDate = new Date(invoice.invoice_date)
    }
    this.setState(nextState)
  }

  handleProductAdd = (productData) => {
    const { onProductAdd, onProductEdit, serverProducts } = this.props
    if (onProductAdd) {
      // Edit/draft mode: delegate to parent which calls the API
      const editingIndex = this.state.editingProductIndex
      if (editingIndex !== null && onProductEdit) {
        onProductEdit(productData, serverProducts[editingIndex])
      } else {
        onProductAdd(productData)
      }
      this.setState({ showProductModal: false, editingProductIndex: null })
      return
    }
    // Create mode: accumulate locally until the whole form is submitted
    const { selectedSaleType, selectedBrand, selectedGroup, selectedCategory, selectedProduct, quantity, unitPrice } = productData
    const product = {
      sale_type: selectedSaleType,
      brand: selectedBrand,
      group: selectedGroup,
      category: selectedCategory,
      product_code: selectedProduct,
      quantity: Number(quantity),
      unit_price: toUnitPrice(unitPrice),
    }
    this.setState(function(prevState) {
      const editingIndex = prevState.editingProductIndex
      const products = editingIndex === null
        ? prevState.products.concat([product])
        : prevState.products.map(function(p, i) { return i === editingIndex ? product : p })
      return { products: products, showProductModal: false, editingProductIndex: null }
    })
  }

  handleProductEdit = (index) => {
    this.setState({ showProductModal: true, editingProductIndex: index })
  }

  // Removal is confirmed first, as in DefaultTemplate: in edit mode it deletes
  // the invoice_detail on the server straight away.
  askProductRemove = (index) => {
    this.setState({ removingProductIndex: index })
  }

  cancelProductRemove = () => {
    this.setState({ removingProductIndex: null })
  }

  handleProductRemove = () => {
    const { onProductRemove, serverProducts } = this.props
    const index = this.state.removingProductIndex
    if (index === null) return
    if (onProductRemove && serverProducts) {
      onProductRemove(serverProducts[index])
      this.setState({ removingProductIndex: null })
      return
    }
    this.setState(function(prevState) {
      return {
        products: prevState.products.filter(function(_, i) { return i !== index }),
        removingProductIndex: null,
      }
    })
  }

  closeProductModal = () => {
    this.setState({ showProductModal: false, editingProductIndex: null })
  }

  headerFields = () => {
    const { number, invoiceDate, saleType, document } = this.state
    return { number, invoiceDate, saleType, document: document || {} }
  }

  // "Guardar rascunho": in edit mode this is a header PUT that leaves the
  // invoice in the draft list; in create mode it creates the draft.
  handleSaveDraft = () => {
    if (this.props.onSaveDraft) return this.props.onSaveDraft(this.headerFields())
    this.handleCreate(false)
  }

  // "Enviar fatura": the header edits still pending in the form are saved
  // before the invoice leaves the draft state, since after set_invoice_to_approval
  // it is read-only and they could no longer be applied.
  handleSubmitFatura = () => {
    if (this.props.onSubmitFatura) return this.props.onSubmitFatura(this.headerFields())
    this.handleCreate(true)
  }

  render() {
    const { campaign, saleTypes, errors, serverProducts, invoice } = this.props
    const { number, saleType, document, invoiceDate, products, showProductModal, editingProductIndex, removingProductIndex } = this.state
    const isEditMode = Boolean(serverProducts)
    const displayProducts = isEditMode ? serverProducts : products
    const editingProduct = editingProductIndex !== null ? displayProducts[editingProductIndex] : null
    // The modal resolves its own "Tipo de venda" from the detail's category
    // ancestry root — it is a client product category, unrelated to the
    // header's sale type.
    const productModalInitialValues = toProductFormInitialValues(editingProduct)

    // Submission is only allowed once the header fields are filled in, a
    // document has been uploaded (locally selected, or already attached in
    // edit mode) and at least one product has been added.
    const hasDocument = Boolean(document) || Boolean(isEditMode && invoice && invoice.attachment)
    const isFormIncomplete = !number || !number.trim()
      || !invoiceDate
      || !hasDocument
      || displayProducts.length === 0

    return (
      <div className="invoice-form-page">
        <CustomButton link tight to="/invoice/created" className="invoice-form-back">
          <ArrowLeftLG className="invoice-form-back-icon" />
          Voltar
        </CustomButton>
        <h4 className="invoice-form-title">{isEditMode ? 'Editar rascunho' : 'Registar fatura'}</h4>

        {/* ── Header fields ── */}
        <div className="invoice-form-card">
          <div className="invoice-form-row">
            <div className="invoice-form-field">
              <label className="invoice-form-label">Nº Fatura</label>
              <input
                className="invoice-form-input"
                type="text"
                placeholder="Insira o nº da fatura"
                value={number}
                onChange={this.updateState('number', this.eventMapper)}
                onBlur={this.checkExistentInvoice}
              />
              {errors && errors.invoice_number && <p className="invoice-error">{errors.invoice_number.join(',')}</p>}
            </div>
            <div className="invoice-form-field">
              <label className="invoice-form-label">Tipo de venda</label>
              <ReactSelect
                onChange={this.updateState('saleType')}
                options={saleTypes}
                getOptionLabel={function(o) { return o.name }}
                getOptionValue={function(o) { return o.id }}
                value={saleType}
                placeholder="Selecionar"
                className="invoice-form-select"
                classNamePrefix="invoice-select"
                noOptionsMessage={function() { return 'Sem opções' }}
              />
              {errors && errors.sale_type && <p className="invoice-error">{errors.sale_type.join(',')}</p>}
            </div>
            <div className="invoice-form-field">
              <InputKeyValue
                title="Data da fatura"
                type="date"
                name="invoiceDate"
                value={invoiceDate}
                maxDate={new Date()}
                onChange={this.updateState('invoiceDate')}
              />
              {errors && errors.invoice_date && <p className="invoice-error">{errors.invoice_date.join(',')}</p>}
            </div>
          </div>
        </div>

        {/* ── Document section ── */}
        <div className="invoice-form-section">
          {!document ? <h5 className="invoice-form-section-title">Documento da fatura</h5> : null}
          {!document && (!invoice || !invoice.attachment) ? <p className="invoice-form-section-subtitle">Formatos aceites: PNG, JPG, JPEG ou PDF</p> : null}
          {isEditMode && invoice && invoice.attachment ? (

            <div className="document-card">
              <div className="document-card-icon-container">
                <div className="document-card-icon">
                  <FileDocument />
                </div>
              </div>
              <div className="document-card-container">
                <div className="document-card-title">{invoice.attachment.url.split('/').pop().split('#')[0].split('?')[0]}</div>
                 <button
                  type="button"
                  className="document-card-download"
                  onClick={function() { window.open(invoice.attachment.url, "_blank") }.bind(this)}
                >
                  <Download />
                  Descarregar
                </button>
              </div>
            </div>

          ) : document ? (
            <div className="document-card">
              <div className="document-card-icon-container">
                <div className="document-card-icon">
                  <FileDocument />
                </div>
              </div>
              <div className="document-card-container">
                <div className="document-card-title">{document.name}</div>
                <button
                  type="button"
                  className="document-card-delete"
                  onClick={function() { this.setState({ document: null }) }.bind(this)}
                >
                  <Trash />
                  Eliminar ficheiro
                </button>
              </div>
            </div>
          ) : (
            <div className="invoice-form-card">
              <FilePicker
                extensions={['jpg', 'jpeg', 'png', 'pdf']}
                onChange={this.updateState('document')}
                onError={function() {}}
              >
                <button type="button" className="gl-button secondary medium body-m">
                  Carregar fatura
                </button>
              </FilePicker>
            </div>
          )}
          {errors && errors.attachment && <p className="invoice-error">{errors.attachment.join(',')}</p>}
        </div>

        {/* ── Products section ── */}
        <div className="invoice-form-section">
          <h5 className="invoice-form-section-title">Produtos</h5>
          {displayProducts.length > 0 ? (
            <div className="invoice-form-products">
              <div className="invoice-form-table-panel">
                <table className="w-full invoice__table">
                  <thead>
                    <tr className="invoice__table-header">
                      <th className="data-table__header invoice__header">Marca</th>
                      <th className="data-table__header invoice__header">Grupo</th>
                      <th className="data-table__header invoice__header">Categoria</th>
                      <th className="data-table__header invoice__header">Código do Produto</th>
                      <th className="data-table__header invoice__header data-table__header--right">Qtd</th>
                      <th className="data-table__header invoice__header data-table__header--center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayProducts.map(function(p, i) {
                      // Handles both the local (create mode) and the server
                      // invoice_detail (edit mode) product shapes.
                      var row = normalizeInvoiceProduct(p)
                      return (
                        <tr key={i} className="invoice__table-row">
                          <td className="invoice__cell">{row.brand || '-'}</td>
                          <td className="invoice__cell">{row.group || '-'}</td>
                          <td className="invoice__cell">{row.category || '-'}</td>
                          <td className="invoice__cell">{row.productCode || '-'}</td>
                          <td className="invoice__cell invoice__cell--right">{row.quantity}</td>
                          <td className="invoice__cell invoice__cell--center">
                            <div className="invoice__actions">
                              <button
                                type="button"
                                className="invoice__action"
                                title="Editar"
                                onClick={function() { this.handleProductEdit(i) }.bind(this)}
                              >
                                <EditPencilLine01 className="invoice__action-icon" />
                              </button>
                              <button
                                type="button"
                                className="invoice__action invoice__action--danger"
                                title="Eliminar"
                                onClick={function() { this.askProductRemove(i) }.bind(this)}
                              >
                                <Trash className="invoice__action-icon" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    }.bind(this))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="invoice-form-add-product gl-button secondary medium body-m"
                onClick={function() { this.setState({ showProductModal: true, editingProductIndex: null }) }.bind(this)}
              >
                Adicionar produto
              </button>
            </div>
          ) : (
            <div className="invoice-form-card">
              <button
                type="button"
                className="gl-button secondary medium body-m"
                onClick={function() { this.setState({ showProductModal: true, editingProductIndex: null }) }.bind(this)}
              >
                Adicionar produto
              </button>
            </div>
          )}
        </div>

        {/* ── Bottom actions ── */}
        <div className="invoice-form-actions">
          <button
            type="button"
            className="gl-button secondary medium body-m"
            disabled={isFormIncomplete}
            onClick={this.handleSaveDraft}
          >
            Guardar rascunho
          </button>
          <button
            type="button"
            className="gl-button primary medium body-m"
            disabled={isFormIncomplete}
            onClick={this.handleSubmitFatura}
          >
            Enviar fatura
          </button>
        </div>

        {/* ── Add/edit product modal ── */}
        <Modal
          show={showProductModal}
          untoggle={this.closeProductModal}
          large
        >
          <div className="gl2026-modal__header">
            <h5 className="gl2026-modal__title">{editingProduct ? 'Editar produto' : 'Adicionar produto'}</h5>
            <button
              type="button"
              className="gl2026-modal__close"
              onClick={this.closeProductModal}
              aria-label="Fechar"
            >×</button>
          </div>
          <InvoiceProductFormContainer
            key={editingProductIndex === null ? 'new' : 'edit-' + editingProductIndex}
            campaign={campaign}
            createMode
            initialValues={productModalInitialValues}
            onSubmit={this.handleProductAdd}
            onCancel={this.closeProductModal}
          />
        </Modal>

        {/* ── Remove product confirm modal ── */}
        <Modal small show={removingProductIndex !== null} untoggle={this.cancelProductRemove}>
          <div className="gl2026-modal__header">
            <h5 className="gl2026-modal__title">Remover produto?</h5>
          </div>
          <div className="gl2026-modal__footer">
            <CustomButton secondary type="button" onClick={this.cancelProductRemove}>Não</CustomButton>
            <CustomButton type="button" onClick={this.handleProductRemove}>Sim</CustomButton>
          </div>
        </Modal>
      </div>
    )
  }
}
